import React, { useState } from 'react';
import { View, TextInput, Text, StyleSheet, TextInputProps, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { borderRadius, spacing, fontSize, fontWeight, accessibility } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

/** Input - themed text input with optional label, icon, error state, and password toggle. */
interface InputProps extends TextInputProps {
  readonly label?: string;
  readonly error?: string;
  readonly icon?: keyof typeof Ionicons.glyphMap;
  readonly accessibilityLabel?: string;
  readonly accessibilityHint?: string;
}

export default function Input({ label, error, icon, style, secureTextEntry, accessibilityLabel, accessibilityHint, ...props }: InputProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = secureTextEntry;

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label} accessibilityRole="text">{label}</Text>}
      <View style={[styles.inputContainer, error && styles.inputError]}>
        {icon && <Ionicons name={icon} size={20} color={colors.textSecondary} style={styles.icon} accessibilityElementsHidden />}
        <TextInput
          style={[styles.input, style]}
          placeholderTextColor={colors.textLight}
          maxLength={32}
          secureTextEntry={isPassword && !showPassword}
          {...(isPassword ? { autoCapitalize: 'none' as const, autoComplete: 'off' as const, textContentType: 'none' as const } : {})}
          accessibilityLabel={accessibilityLabel || label}
          accessibilityHint={accessibilityHint}
          accessibilityState={{ disabled: props.editable === false }}
          {...props}
        />
        {isPassword && (
          <TouchableOpacity
            onPress={() => setShowPassword(!showPassword)}
            style={styles.eyeIcon}
            accessibilityRole="button"
            accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name={showPassword ? 'eye-off' : 'eye'} size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>
      {error && <Text style={styles.error} accessibilityRole="alert" accessibilityLiveRegion="polite">{error}</Text>}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { marginBottom: spacing.md },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.xs },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: c.border,
    paddingHorizontal: spacing.md,
    minHeight: accessibility.minTouchTarget,
  },
  inputError: { borderColor: c.danger },
  icon: { marginRight: spacing.sm },
  input: {
    flex: 1,
    fontSize: fontSize.md,
    color: c.text,
    paddingVertical: spacing.md - 2,
  },
  eyeIcon: {
    padding: spacing.xs,
    minWidth: accessibility.minTouchTarget,
    minHeight: accessibility.minTouchTarget,
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: { fontSize: fontSize.xs, color: c.danger, marginTop: spacing.xs },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
